<template>
  <div
    class="d-flex align-items-center"
    v-if="item.status === 'aguardando'"
  >
    <Button
      :marginVertical="false"
      fullWidth
      @onClick="handleCancelPressed"
      label="Cancelar"
      outline
      outlineColor="danger"
      :loading="isSubmittingCancel"
      class="me-2"
    />
    <div class="ms-2 flex-fill">
      <Button
        :marginVertical="false"
        fullWidth
        @onClick="handleAccept"
        label="Aceitar sessão"
        :loading="isSubmittingConfirm"
        :disabled="item.payment_requires_action"
      />
      <p v-if="item.payment_requires_action" class="text-warning text-label mb-0 mt-1 text-center">
        Aguardando autenticação do pagamento pelo paciente
      </p>
    </div>
  </div>

  <div
    class="d-flex align-items-center justify-content-between"
    v-if="item.status === 'agendado' || item.status === 'em andamento'"
  >
    <p class="text-subtitle mb-0">
      {{ item.status === 'em andamento' ? `Sessão iniciada` : `Aguardando início` }}
    </p>
    <Button
      :marginVertical="false"
      @onClick="handleScheduledAppointment"
      label="Iniciar sessão"
      :disabled="!item.can_enter"
      :loading="isSubmittingConfirm"
    />
  </div>

  <div
    class="d-flex align-items-center justify-content-between"
    v-if="
      item.type === 'imediate' &&
      (item.status === 'em andamento' || item.status === 'pendente')
    "
  >
    <Button
      :marginVertical="false"
      v-if="item.status === 'pendente'"
      fullWidth
      outline
      class="me-2"
      @onClick="handleCancelPressed"
      :loading="isSubmittingCancel"
      label="Recusar"
    />
    <p v-else class="text-subtitle mb-0">Sessão iniciada</p>

    <Button
      :marginVertical="false"
      :fullWidth="item.status === 'pendente'"
      :class="{ 'ms-2': item.status === 'pendente' }"
      :label="item.status === 'pendente' ? 'Iniciar sessão' : 'Retomar sessão'"
      :loading="comingIn"
      :disabled="!item.can_enter"
      @onClick="handleScheduledAppointment"
    />
  </div>

  <ModalComponent
    backdrop="false"
    :id="modalId"
    title="Deseja aceitar esta sessão e as futuras?"
  >
    <template v-slot:body>
      <p class="text-paragraph">
        Aceitando essa sessão você estará aceitando sessões que acontecerão
        {{ item.recurrency?.frequencyText }}
      </p>

      <div
        class="d-flex py-4 align-items-center justify-content-between border-bottom"
      >
        <p class="text-paragraph mb-0">Período</p>
        <p class="text-subtitle mb-0">{{ item.recurrency?.frequencyLabel }}</p>
      </div>
    </template>
    <template v-slot:footer>
      <Button outline label="Não, voltar" @onClick="toggleModal(modalId)" />
      <Button
        label="Aceitar esta e as próximas sessões"
        @onClick="handleConfirmPressed"
        :loading="isSubmittingConfirm"
      />
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import { ref } from "vue";
import type { Appointment } from "@phurshell/psiapp-frontend-core/domain";
import { useRouter } from "vue-router";
import { toggleModal } from "@/utils/Helper";
import { EventBus, EventBusNames } from "@/utils/EventBus";
import { syncSession } from "@/di/useCases";
import { appointmentRepo } from "@/di/repositories";
import { useUpdateAppointment, useInvalidateAppointments } from "@/composables/useAppointments";
import { closeOffCanvas } from "@/views/OffcanvasViews/OffcanvasViewsConfig";
import paths from "@/router/paths";

const props = defineProps<{ item: Appointment }>();

const router = useRouter();

const modalId = "acceptance_modal";
const isSubmittingCancel = ref(false);
const isSubmittingConfirm = ref(false);
const comingIn = ref(false);

const { mutateAsync: updateAppointment } = useUpdateAppointment();
const invalidateAppointments = useInvalidateAppointments();

function handleCancelPressed() {
  toggleModal("cancel_appointment_modal");
}

function handleAccept() {
  if (props.item.recurrency) {
    handleAcceptPressed();
  } else {
    handleConfirmPressed();
  }
}

async function handleScheduledAppointment() {
  try {
    if (props.item.type === "imediate") {
      comingIn.value = true;
      await appointmentRepo.getAppointmentById(props.item.id);
      handleGoToRoom();
    } else {
      handleGoToRoom();
    }
  } catch (error) {
    console.error(error);
  } finally {
    comingIn.value = false;
  }
}

function handleGoToRoom() {
  router.push(`${paths.appointments.room}/${props.item.id}`);
  closeOffCanvas();
}

async function handleAcceptPressed() {
  try {
    if (isSubmittingConfirm.value || isSubmittingCancel.value) return;
    const params = {
      interval: props.item.recurrency.interval,
      frequency: props.item.recurrency.frequency,
      date: props.item.recurrency.reference_date,
    };
    const res = await appointmentRepo.recurrenciesVerification(params);
    if (res.can_schedule) {
      toggleModal(modalId);
    } else {
      alert(
        res.message ??
          "Ops! Horário já possui outras sessões agendadas. O horário solicitado coincide com uma sessão já agendada no futuro",
      );
    }
  } catch (error) {
    console.log(error);
  }
}

async function handleConfirmPressed() {
  try {
    if (isSubmittingConfirm.value || isSubmittingCancel.value) return;
    isSubmittingConfirm.value = true;

    await updateAppointment({ id: props.item.id, status: "agendado" });
    EventBus.$emit(EventBusNames.UPDATE_APPOINTMENTS);
    await syncSession.execute();

    if (props.item.recurrency) toggleModal(modalId);
  } catch (error) {
    console.error(error);
  } finally {
    isSubmittingConfirm.value = false;
  }
}
</script>
